/**
 * PrintStudioShell — Shared layout shell for Print Studio pages.
 * Provides the floating toolbar, preview area, and customize sidebar slot.
 * Used by both PrintStudioPage (read-only ERPNext docs) and DocumentCreatorPage (editable free-form).
 */

import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

interface PrintStudioShellProps {
  /** Optional label shown next to back button (e.g. document name) */
  backLabel?: string;
  /** Center toolbar content (template/doc-type selector) */
  centerSlot: ReactNode;
  /** Right toolbar actions (grouped in pill) */
  actionsSlot: ReactNode;
  /** Main preview/editor content */
  children: ReactNode;
  /** Sidebar panel (e.g. TemplateSettingsPanel) — rendered beside children */
  sidebar?: ReactNode;
  /** Background image URL for the workspace area behind the paper */
  workspaceBg?: string;
}

export function PrintStudioShell({
  backLabel,
  centerSlot,
  actionsSlot,
  children,
  sidebar,
  workspaceBg,
}: PrintStudioShellProps) {
  const navigate = useNavigate();

  const hasBg = !!workspaceBg;

  return (
    <div
      className="relative flex h-full w-full flex-col gap-2 p-3"
      style={
        hasBg
          ? {
              backgroundImage: `url(${workspaceBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {/* Header — individual frosted glass buttons when background is active */}
      <div className="flex items-center gap-2">
        {/* Left: back */}
        <div className={`flex items-center shrink-0 rounded-full bg-white dark:bg-black/50 shadow-sm ${
          hasBg ? 'backdrop-blur-xl bg-white/80' : ''
        }`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-8 w-8 shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
        </div>
        {backLabel && (
          <span className="text-xs text-muted-foreground">{backLabel}</span>
        )}

        {/* Center */}
        <div className="flex flex-1 items-center justify-center gap-3">
          <div className={`rounded-full bg-white dark:bg-black/50 shadow-sm ${hasBg ? 'backdrop-blur-xl bg-white/80' : ''}`}>
            {centerSlot}
          </div>
        </div>

        {/* Right: individual action buttons — each gets white pill bg */}
        <div className="flex items-center gap-1.5 shrink-0 [&>*]:rounded-full ">
          {actionsSlot}
        </div>
      </div>

      {/* Preview + Sidebar */}
      <div className="relative flex flex-1 min-h-0">
        <div className="relative flex-1 overflow-hidden rounded-xl">
          <div
            className="h-full overflow-auto p-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={hasBg ? undefined : { backgroundColor: 'rgb(243 244 246)' }}
          >
            {children}
          </div>
        </div>
        {sidebar}
      </div>
    </div>
  );
}
